import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  Select,
  RadioItem,
} from '@checkatrade/web-ui';
import { useIsMobileScreen } from '@checkatrade/web-utils';
import { useTypedRouter } from '@hooks/use-typed-router';
import { sortSearchResults } from 'containers/SearchContainer/actions';
import { track } from 'lib/sdks/analytics';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { z } from 'zod';

const sortAlgorithms = {
  SORT_BY_REVIEW_SCORE: 'sort_by_reviews_score',
} as const;

const sortByOptions = [
  {
    label: 'Most relevant',
    value: '',
    trackId: 'mostRelevant',
  },
  {
    label: 'Highest rated',
    value: sortAlgorithms.SORT_BY_REVIEW_SCORE,
    trackId: 'highestRated',
  },
];

export function SearchSorter({
  categoryName,
  categoryId,
  postcode,
  algorithm = '',
  setAlgorithm,
}: {
  categoryName: string;
  categoryId: number;
  postcode: string;
  algorithm?: string;
  setAlgorithm: Dispatch<SetStateAction<string | undefined>>;
}) {
  const router = useTypedRouter(z.object({ algorithm: z.string().optional() }));
  const isMobile = useIsMobileScreen();
  const dispatch = useDispatch();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerValue, setDrawerValue] = useState(algorithm);

  function handleSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const selectedValue = event.target.value;
    sort(selectedValue);
  }

  function sort(selectedValue: string) {
    const sortOption =
      sortByOptions.find(option => option.value === selectedValue) ??
      sortByOptions[0];

    track('search.sort', {
      sort_by: sortOption?.trackId,
      trade_category: categoryId,
      postcode: postcode,
    });
    setAlgorithm(selectedValue);
    setDrawerValue(selectedValue);

    dispatch(
      sortSearchResults({
        categorySlug: categoryName,
        location: postcode.replace('-', ' '),
        algorithm: selectedValue,
        deviceId: localStorage.getItem('deviceId') || '',
      }),
    );
    const [pathname, queryParams] = router.asPath.split('?');

    const query = new URLSearchParams(queryParams);

    if (selectedValue === '') {
      query.delete('algorithm');
    } else {
      query.set('algorithm', selectedValue);
    }

    if (query.toString() === '') {
      return router.push(pathname, undefined, { shallow: true });
    }

    router.push(
      pathname,
      { pathname, query: query.toString() },
      { shallow: true },
    );
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setDrawerValue(event.target.value ?? '');
  }

  function closeAndSort() {
    setDrawerOpen(false);
    sort(drawerValue);
  }

  const sortAlgo =
    sortByOptions.find(option => option.value === algorithm) ||
    sortByOptions[0];

  if (isMobile) {
    return (
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerTrigger>
          <Button className="ml-auto mt-4 bg-accent-soft font-semibold text-secondary">
            Sort by: {sortAlgo.label}
          </Button>
        </DrawerTrigger>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Sort By</DrawerTitle>
          </DrawerHeader>
          <DrawerBody>
            {sortByOptions.map(option => (
              <RadioItem
                key={option.value}
                label={option.label}
                name="sortBy"
                value={option.value ?? ''}
                onChange={handleChange}
                checked={drawerValue === option.value}
              />
            ))}
          </DrawerBody>
          <DrawerFooter>
            <Button onClick={() => setDrawerValue(algorithm)}>Reset</Button>
            <Button className="flex-1" onClick={closeAndSort}>
              Show results
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/label-has-for
    <label className="ml-auto flex min-w-52 items-center overflow-hidden rounded-full border-2 border-accent-soft bg-accent-soft !fill-secondary px-3">
      <span className="min-w-fit font-semibold text-secondary">Sort by:</span>
      <Select
        name="sortBy"
        onChange={handleSelect}
        ariaLabel=""
        value={sortAlgo.value}
        size="sm"
        options={sortByOptions}
        className={{
          selectWrapper:
            'border-none bg-accent-soft pl-3 font-semibold text-secondary',
        }}
      />
    </label>
  );
}
